// Pure helpers for the BA portfolio screens (BA P1.6a). No React, no I/O.
import type { BaContent, FieldVerification, MyBaProfile, ReviewerBaProfile } from '@/services/baProfileService';

/** Recorded with the BA's consent at submit. Bump when the consent text changes. */
export const BA_CONSENT_VERSION = 'ba-publication-2026-11-03-r2'; // r2: retention periods

/** Owner AC4, word for word: what removal (by her, by the company, or on
 *  offboarding) does. Used by the consent text, the "Remove me" confirmation and
 *  the offboarding help. */
export const BA_REVOCATION_TEXT =
  'Your profile disappears from the catalogue at once and no new image links are issued. ' +
  'Image links already given out may keep working for up to 15 minutes.';

/** Owner-approved retention (3 Nov 2026; employee-service ba_retention.py):
 *  what happens to her photos after she removes her profile or leaves. */
export const BA_RETENTION_TEXT =
  'If I remove my profile, my photos are deleted after 30 days. If I stop working with {{company}}, ' +
  'the photos as I uploaded them are deleted after 30 days and my portrait and photos after 90 days, ' +
  'unless I come back and submit my profile again before then.';

/** What she agrees to (English default; the i18n key `consent.text` carries
 *  {{company}}). Names that clients may already have seen it, and the AC4
 *  removal bound (P1.4: 15-minute image links), and the retention periods. */
export const BA_CONSENT_TEXT =
  'I agree that this profile, including my portrait and photos once {{company}} approves them, may be shown to ' +
  '{{company}}’s clients in the Brand Ambassador catalogue, and that clients may already have seen it by the time ' +
  'I remove it. I can remove it at any time with “Remove me from the catalogue”. ' + BA_REVOCATION_TEXT + ' ' +
  BA_RETENTION_TEXT;

export const CONTENT_FIELDS = ['display_name', 'bio', 'skills', 'languages', 'service_areas', 'experience'] as const;
export type ContentField = (typeof CONTENT_FIELDS)[number];

/** English labels (the i18n defaults for `fields.<key>`). */
export const FIELD_LABELS: Record<ContentField, string> = {
  display_name: 'Display name', bio: 'Introduction', skills: 'Skills', languages: 'Languages',
  service_areas: 'Service areas', experience: 'Experience',
};

/** Required to submit (employee-service REQUIRED_TO_SUBMIT). */
export const REQUIRED_TO_SUBMIT: ContentField[] = ['display_name', 'bio', 'skills', 'languages', 'service_areas'];

const filled = (v: unknown) => (Array.isArray(v) ? v.length > 0 : typeof v === 'string' ? v.trim() !== '' : v != null);

export function missingForSubmit(draft: BaContent): ContentField[] {
  return REQUIRED_TO_SUBMIT.filter((k) => !filled((draft as any)[k]));
}

/** "a, b ,, c" → ["a","b","c"] (trimmed, empties dropped). */
export function splitList(s: string): string[] {
  return s.split(',').map((x) => x.trim()).filter(Boolean);
}

const norm = (v: unknown) => JSON.stringify(v ?? null);

/** Normalize one content value as the server stores it: empty → absent. */
const canon = (v: unknown) => (v === '' || v == null || (Array.isArray(v) && v.length === 0) ? null : v);

/** Owner AC1: the PATCH body — ONLY the content fields whose value differs from
 *  the saved draft. Never media_ids / portrait_media_id (a text edit can't touch
 *  images). A field the BA emptied is sent as "" / [] (an explicit clear). */
export function draftChanges(edited: BaContent, saved: BaContent | null | undefined): Partial<BaContent> {
  const out: Partial<BaContent> = {};
  for (const k of CONTENT_FIELDS) {
    const now = canon((edited as any)[k]);
    if (norm(now) === norm(canon((saved as any)?.[k]))) continue;
    (out as any)[k] = now ?? (k === 'display_name' || k === 'bio' ? '' : []);
  }
  return out;
}

/** Fields whose submitted value differs from the live (published) one. With
 *  nothing live, every filled field counts as changed. */
export function changedFields(submitted: BaContent | null | undefined, live: BaContent | null | undefined): ContentField[] {
  if (!submitted) return [];
  return CONTENT_FIELDS.filter((k) => norm((submitted as any)[k]) !== norm((live as any)?.[k]))
    .filter((k) => live || filled((submitted as any)[k]));
}

export type Verification = 'ba_provided' | 'fern_verified';

/** A field's label, read ONLY from the API's verification (the client view's,
 *  or the draft view's). Anything unmarked — and always bio and display name,
 *  the BA's own words — is BA-provided. Never computed client-side. */
export function verificationOf(field: ContentField, v?: FieldVerification | null, item?: number): Verification {
  if (field === 'bio' || field === 'display_name' || !v) return 'ba_provided';
  const raw = field === 'experience' ? (item === undefined ? undefined : v.experience?.[item]) : (v as any)[field];
  return raw === 'fern_verified' ? 'fern_verified' : 'ba_provided';
}

/** Fields a reviewer may tick as verified (never the BA's own words). */
export const VERIFIABLE_FIELDS: ContentField[] = ['skills', 'languages', 'service_areas'];

/** What a preview shows: the BA's draft, the live version, or (reviewer) a revision that isn't live. */
export type PreviewSource = 'draft' | 'published' | 'revision';

export interface PortfolioStatus {
  live: { revisionNo: number; publishedAt: string | null } | null;
  underReview: { revisionNo: number; submittedAt: string | null } | null;
  changesRequested: { revisionNo: number; reason: string | null } | null;
  approvedNotLive: { revisionNo: number } | null; // approved, waiting to be published
  removed: boolean; // was published, now unpublished
}

/** The status banner: what clients see now, and what's with Fern. */
export function portfolioStatus(p: MyBaProfile | ReviewerBaProfile): PortfolioStatus {
  const live = p.publication?.status === 'published' && p.published_revision
    ? { revisionNo: p.published_revision.revision_no, publishedAt: p.publication.published_at ?? null }
    : null;
  const underReview = p.submitted_revision
    ? { revisionNo: p.submitted_revision.revision_no, submittedAt: p.submitted_revision.submitted_at }
    : null;
  const last = (p as MyBaProfile).last_decision;
  const changesRequested = p.draft_status === 'changes_requested'
    ? { revisionNo: last?.revision_no ?? 0, reason: last?.reason ?? null }
    : null;
  // A live revision keeps status "approved", so "approved, not live" is the
  // newest approved revision when it isn't the published one.
  const newestApproved = 'revisions' in p
    ? p.revisions.find((r) => r.status === 'approved')?.revision_no
    : last?.decision === 'approved' ? last.revision_no : undefined;
  const approvedNotLive = !underReview && newestApproved !== undefined && newestApproved !== live?.revisionNo
    ? { revisionNo: newestApproved } : null;
  return { live, underReview, changesRequested, approvedNotLive, removed: !live && !!p.publication?.unpublished_at };
}
